/**
 * NJMMISMFAContactCard — admin settings panel for the per-org NJMMIS
 * MFA contact (cell phone + email + preferred channel) and the
 * intercept feature flag.
 *
 * Where it lives: rendered inside the NJMMIS tab of IntegrationSettings.
 * Audience: billing-role admins per the backend route guard.
 *
 * Behavior:
 *   * GET  /api/njmmis/mfa-contact  — load current values
 *   * PUT  /api/njmmis/mfa-contact  — save changes (also flips the
 *     feature flag when the operator toggles "Enable MFA intercept")
 *   * POST /api/njmmis/mfa-contact/test — send a real SMS + email to
 *     verify the channel actually works BEFORE flipping the flag on.
 *
 * The intercept flag stays OFF by default — turning it on without
 * a working contact would just queue OTPs that nobody answers.
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Shield, ShieldCheck, Send, Loader2, AlertTriangle, CheckCircle2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NJMMISMFAContactCard = ({ token, toast }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [interceptEnabled, setInterceptEnabled] = useState(false);
  const [cellPhone, setCellPhone] = useState('');
  const [email, setEmail] = useState('');
  const [contactName, setContactName] = useState('');
  const [preferredChannel, setPreferredChannel] = useState('sms');
  const [maskedView, setMaskedView] = useState({ phone: '', email: '' });
  const [testResult, setTestResult] = useState(null);

  const fetchSettings = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/njmmis/mfa-contact`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const c = r.data?.contact || {};
      setInterceptEnabled(!!r.data?.intercept_enabled);
      setCellPhone(c.cell_phone || '');
      setEmail(c.email || '');
      setContactName(c.contact_name || '');
      setPreferredChannel(c.preferred_channel || 'sms');
      setMaskedView({ phone: c.cell_phone_masked || '', email: c.email_masked || '' });
    } catch (e) {
      // silent — settings just don't populate
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const save = async () => {
    if (!token) return;
    setSaving(true);
    setTestResult(null);
    try {
      const r = await axios.put(
        `${API_URL}/api/njmmis/mfa-contact`,
        {
          cell_phone: cellPhone,
          email,
          contact_name: contactName,
          preferred_channel: preferredChannel,
          intercept_enabled: interceptEnabled,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const c = r.data?.contact || {};
      setMaskedView({ phone: c.cell_phone_masked || '', email: c.email_masked || '' });
      if (toast) toast.success('MFA contact saved');
    } catch (e) {
      if (toast) toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    if (!token) return;
    setSendingTest(true);
    setTestResult(null);
    try {
      const r = await axios.post(
        `${API_URL}/api/njmmis/mfa-contact/test`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setTestResult(r.data?.delivery || {});
      if (toast) toast.success('Test message sent');
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Test send failed';
      setTestResult({ error: typeof detail === 'string' ? detail : 'Test send failed' });
      if (toast) toast.error(detail);
    } finally {
      setSendingTest(false);
    }
  };

  if (loading) {
    return (
      <Card data-testid="njmmis-mfa-card-loading" className="border-2 border-slate-200">
        <CardContent className="py-8 flex items-center justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
        </CardContent>
      </Card>
    );
  }

  const hasContact = !!(cellPhone || email);

  return (
    <Card
      data-testid="njmmis-mfa-contact-card"
      className="border-2 border-amber-200 bg-amber-50/30 mt-4"
    >
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 text-amber-900">
              <Shield className="w-5 h-5" />
              NJMMIS MFA Intercept (Nov 13 readiness)
            </CardTitle>
            <CardDescription className="mt-1">
              When NJMMIS requires a verification code, CounselCore relays it to this
              contact and resumes your claim submission automatically. Configure the
              contact below, then send a test message before enabling the intercept.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-amber-900 font-medium">
              {interceptEnabled ? 'Enabled' : 'Disabled'}
            </span>
            <Switch
              data-testid="njmmis-mfa-intercept-toggle"
              checked={interceptEnabled}
              onCheckedChange={setInterceptEnabled}
            />
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="mfa-contact-name">Contact name</Label>
            <Input
              data-testid="njmmis-mfa-contact-name"
              id="mfa-contact-name"
              value={contactName}
              onChange={(e) => setContactName(e.target.value)}
              placeholder="Pete Bendig"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="mfa-preferred-channel">Preferred channel</Label>
            <select
              data-testid="njmmis-mfa-preferred-channel"
              id="mfa-preferred-channel"
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
              value={preferredChannel}
              onChange={(e) => setPreferredChannel(e.target.value)}
            >
              <option value="sms">SMS</option>
              <option value="email">Email</option>
            </select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="mfa-cell-phone">Cell phone (E.164: +12015551234)</Label>
            <Input
              data-testid="njmmis-mfa-cell-phone"
              id="mfa-cell-phone"
              value={cellPhone}
              onChange={(e) => setCellPhone(e.target.value)}
              placeholder="+12015551234"
            />
            {maskedView.phone && (
              <p className="text-xs text-slate-500">Saved as <span className="font-mono">{maskedView.phone}</span></p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="mfa-email">Email</Label>
            <Input
              data-testid="njmmis-mfa-email"
              id="mfa-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="billing@yourorg.com"
            />
            {maskedView.email && (
              <p className="text-xs text-slate-500">Saved as <span className="font-mono">{maskedView.email}</span></p>
            )}
          </div>
        </div>

        {interceptEnabled && !hasContact && (
          <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded p-3 text-sm">
            <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
            <span className="text-red-900">
              Intercept is enabled but no contact is configured. The MFA banner will
              appear in-app but nobody will be paged. Add a cell phone or email above.
            </span>
          </div>
        )}

        {testResult && !testResult.error && (
          <div
            data-testid="njmmis-mfa-test-result"
            className="bg-emerald-50 border border-emerald-200 rounded p-3 text-sm space-y-1"
          >
            <div className="flex items-center gap-2 text-emerald-900 font-medium">
              <CheckCircle2 className="w-4 h-4" />
              Test message sent
            </div>
            {testResult.sms && (
              <div className="text-xs text-emerald-800">
                SMS: {testResult.sms.success ? 'delivered' : `failed (${testResult.sms.error || 'unknown'})`}
              </div>
            )}
            {testResult.email && (
              <div className="text-xs text-emerald-800">
                Email: {testResult.email.success ? 'delivered' : `failed (${testResult.email.error || 'unknown'})`}
              </div>
            )}
          </div>
        )}

        {testResult?.error && (
          <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-900">
            {testResult.error}
          </div>
        )}

        <div className="flex flex-wrap gap-2 pt-2 border-t border-amber-200">
          <Button
            data-testid="njmmis-mfa-save-button"
            onClick={save}
            disabled={saving}
            className="bg-amber-700 hover:bg-amber-800"
          >
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin mr-2" />}
            <ShieldCheck className="w-4 h-4 mr-2" />
            Save MFA contact
          </Button>
          <Button
            data-testid="njmmis-mfa-test-button"
            onClick={sendTest}
            disabled={sendingTest || !hasContact}
            variant="outline"
          >
            {sendingTest && <Loader2 className="w-3.5 h-3.5 animate-spin mr-2" />}
            <Send className="w-4 h-4 mr-2" />
            Send test message
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default NJMMISMFAContactCard;
